import React, { FC } from "react";
import { Box, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import Badge from "../../../../../assets/hamaspik/Badge";
import { formatPifDate, parsePifDate } from "../../pifDates";
import { pifFieldId } from "../../pifFocus";
import { PifFlow, PifFormValues } from "../../types";
import PifField from "./PifField";
import PifSectionCard from "./PifSectionCard";

interface PifSectionApplicantProps {
  flow: PifFlow;
  values: PifFormValues;
  onChange: (patch: Partial<PifFormValues>) => void;
}

/**
 * XXX-XX-XXXX as the digits accumulate. Deliberately not PifStartScreen's `formatLookupInput`,
 * which leaves six digits or fewer unformatted because that box also accepts an Employee ID —
 * here every entry is an SSN, so it is grouped from the fourth digit on.
 */
const formatSsn = (raw: string): string => {
  const d = raw.replace(/\D/g, "").slice(0, 9);
  if (d.length <= 3) return d;
  if (d.length <= 5) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
};

const APPLICANT_NOTE: Record<PifFlow, string> = {
  default: "",
  new: "Exactly as it appears on their government ID.",
  active: "These come from the employee record. If something's wrong, the employee changes it in self-service — not here.",
  inactive: "Pulled from their old record. Confirm the spelling out loud; names and details drift over the years.",
};

const PifSectionApplicant: FC<PifSectionApplicantProps> = ({ flow, values: f, onChange }) => {
  const lockPersonal = flow === "active";
  const digits = f.q.replace(/\D/g, "");
  // Open only when the form does not already know the SSN: a new applicant whose lookup entry was
  // a hiring number, not a nine-digit SSN (Bug 82668). A nine-digit entry is the SSN, so it stays
  // masked and locked exactly as before, and so does every flow that reads it off an enterprise
  // record (AC3/AC4).
  const editableSsn = flow === "new" && !f.ssnFromLookup;

  return (
    <PifSectionCard
      id="sec-applicant"
      title="Applicant"
      note={APPLICANT_NOTE[flow]}
      badge={lockPersonal ? <Badge size="sm">FROM RECORD</Badge> : undefined}
    >
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 1.75 }}>
        {/* The ids are what a validation error focuses — see pifFocus / getPifGaps. */}
        <PifField label="First name *">
          <TextField id={pifFieldId("firstName")} size="small" fullWidth value={f.firstName} disabled={lockPersonal} onChange={(e) => onChange({ firstName: e.target.value })} />
        </PifField>
        <PifField label="Middle name">
          <TextField size="small" fullWidth value={f.middleName} disabled={lockPersonal} onChange={(e) => onChange({ middleName: e.target.value })} />
        </PifField>
        <PifField label="Last name *">
          <TextField id={pifFieldId("lastName")} size="small" fullWidth value={f.lastName} disabled={lockPersonal} onChange={(e) => onChange({ lastName: e.target.value })} />
        </PifField>
        <PifField label="Known as">
          <TextField size="small" fullWidth value={f.knownAs} placeholder="Preferred name, optional" onChange={(e) => onChange({ knownAs: e.target.value })} />
        </PifField>
        <PifField label="Date of birth *">
          <DatePicker
            value={parsePifDate(f.dob)}
            disabled={flow !== "new"}
            onChange={(value) => onChange({ dob: formatPifDate(value) })}
            slotProps={{
              textField: { id: pifFieldId("dob"), size: "small", fullWidth: true, inputProps: { readOnly: true } },
            }}
          />
        </PifField>
        <PifField label="Social Security Number">
          {editableSsn ? (
            <TextField
              id={pifFieldId("q")}
              size="small"
              fullWidth
              value={f.q}
              placeholder="XXX-XX-XXXX"
              inputMode="numeric"
              autoComplete="off"
              onChange={(e) => onChange({ q: formatSsn(e.target.value) })}
              inputProps={{ maxLength: 11, "aria-label": "Social Security Number" }}
              data-testid="postif-ssn"
            />
          ) : (
            <TextField
              size="small"
              fullWidth
              disabled
              // The last four are shown back only to a new applicant's own hiring supervisor, who
              // typed them a moment ago on the start screen, as a check the right digits carried
              // over. Read off an enterprise record it is fully hidden, per Epic 79820 AC3.
              value={flow === "new" ? `•••-••-${digits.slice(-4)}` : "•••-••-••••"}
              inputProps={{ "data-masked": true, "aria-label": "Social Security Number, hidden" }}
              data-testid="postif-ssn"
            />
          )}
        </PifField>
      </Box>
    </PifSectionCard>
  );
};

export default PifSectionApplicant;
